import { useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { Button } from "@akuna/ui";
import { useTranslation } from "react-i18next";
import type { StayPlan } from "@/hooks/useStayPlans";
import { ModuleBadge, SectionCard } from "../accommodations/primitives";
import DeleteStayPlanDialog from "./DeleteStayPlanDialog";

export default function StayPlanCard({
  plan,
  canEdit,
  onEdit,
  canDelete,
  money,
  showModuleBadge,
}: {
  plan: StayPlan;
  canEdit: boolean;
  onEdit: () => void;
  canDelete: boolean;
  money: (value: number) => string;
  showModuleBadge?: boolean;
}) {
  const { t } = useTranslation("daycareHotel");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const ns = plan.kind === "day_package" ? "packages" : "plans";

  return (
    <SectionCard className="p-4">
      <div className="flex items-start gap-3">
        <div>
          {showModuleBadge ? (
            <div className="mb-1.5">
              <ModuleBadge tone={plan.module}>
                {t(plan.module === "hotel" ? "plans.report.moduleHotel" : "plans.report.moduleDaycare")}
              </ModuleBadge>
            </div>
          ) : null}
          <h3 className="font-display text-[15px] font-bold">{plan.name}</h3>
          <p className="mt-1 text-[12.5px] text-color-light">
            {plan.kind === "day_package"
              ? t("plans.dayPackageSummary", { count: plan.includedDays })
              : t(`plans.rules.${plan.usageRule}${plan.usageRule === "per_week" && plan.weekInterval === 2 ? "_biweekly" : ""}`, { count: plan.usageLimit })}
          </p>
        </div>
        {canEdit || canDelete ? (
          <div className="ml-auto flex items-center gap-1.5">
            {canEdit ? (
              <Button size="sm" variant="outline" onClick={onEdit} data-testid={`stay-plan-edit-${plan.id}`}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            ) : null}
            {canDelete ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDeleteOpen(true)}
                data-testid={`stay-plan-delete-${plan.id}`}
                aria-label={t(`${ns}.deleteAction`)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
        <div>
          {plan.priceWithOtherModulePlan !== null ? (
            <p className="text-[11px] font-semibold uppercase tracking-wide text-color-light">
              {t("plans.priceStandard")}
            </p>
          ) : null}
          <p className="font-display text-xl font-extrabold">{money(plan.price)}</p>
        </div>
        {plan.priceWithOtherModulePlan !== null ? (
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-color-light">
              {t("plans.priceWithActivePlan")}
            </p>
            <p className="font-display text-xl font-extrabold">{money(plan.priceWithOtherModulePlan)}</p>
          </div>
        ) : null}
      </div>
      {canDelete ? (
        <DeleteStayPlanDialog open={deleteOpen} onOpenChange={setDeleteOpen} module={plan.module} plan={plan} />
      ) : null}
    </SectionCard>
  );
}
